import { httpClient } from '@/core/api/httpClient';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';
import i18n from '@/core/i18n/config';
import { aiIdempotencyStore } from './ai.idempotency';
import { mockQuickTaskApply, mockQuickTaskRequest } from './ai.mock';
import type {
  ApiApplyRequest,
  ApiApplyResponse,
  ApiProposeRequest,
  ApiSpeechToTextResponse,
  ApiTaskPlanApplyRequest,
  ApiTaskPlanApplyResponse,
  ApiTaskPlanProposalResponse,
  ApiTaskPlanProposeRequest,
  ApiTaskProposal,
} from './ai.contract';
import type {
  ApplyResult,
  ApplyTaskPlanResult,
  BoardContext,
  QuickTaskProposal,
  TaskPlanProposal,
} from '../types';
import { fromApiProposal } from '../adapters/proposal.adapter';

export function aiProposePath(projectId: string, boardId: string): string {
  return `${API_V1}/projects/${projectId}/boards/${boardId}/ai/tasks/proposal`;
}

export function aiApplyPath(projectId: string, boardId: string): string {
  return `${API_V1}/projects/${projectId}/boards/${boardId}/ai/tasks`;
}

export function speechToTextPath(): string {
  return `${API_V1}/ai/speech-to-text`;
}

export function aiTaskPlanProposalPath(projectId: string, boardId: string): string {
  return `${API_V1}/projects/${projectId}/boards/${boardId}/ai/task-plan/proposal`;
}

export function aiTaskPlanPath(projectId: string, boardId: string): string {
  return `${API_V1}/projects/${projectId}/boards/${boardId}/ai/task-plan`;
}

/**
 * Service for the AI quick-create flow.
 * Mirrors tasks.service: checks the mock flag per call, delegates to the
 * mock fixtures when enabled, otherwise posts through the httpClient.
 */
export const aiService = {
  async proposeQuickTask(
    projectId: string,
    boardId: string,
    instruction: string,
    boardContext: BoardContext | null,
    timeZoneId: string,
  ): Promise<QuickTaskProposal> {
    if (env.VITE_USE_MOCK === 'true') {
      return mockQuickTaskRequest(instruction);
    }

    const context: ApiProposeRequest['context'] = boardContext
      ? {
          statuses: boardContext.statuses,
          groups: boardContext.groups,
          members: boardContext.members.map((m) => ({ id: m.id, displayName: m.name })),
          tags: boardContext.tags,
          callerLanguage: i18n.language ?? null,
        }
      : null;

    const body: ApiProposeRequest = {
      instruction,
      callerTimeZoneId: timeZoneId,
      context,
    };

    const { data } = await httpClient.post<ApiProposeRequest, ApiTaskProposal>(
      aiProposePath(projectId, boardId),
      body,
    );
    return fromApiProposal(data);
  },

  async applyQuickTask(
    projectId: string,
    boardId: string,
    request: ApiApplyRequest,
  ): Promise<ApplyResult> {
    if (env.VITE_USE_MOCK === 'true') {
      return mockQuickTaskApply(request);
    }

    const idempotencyKey = aiIdempotencyStore.getKey(projectId, boardId, 'quick-task');
    const { data } = await httpClient.post<ApiApplyRequest, ApiApplyResponse>(
      aiApplyPath(projectId, boardId),
      request,
      { headers: { 'X-Idempotency-Key': idempotencyKey } },
    );
    return data;
  },

  async proposeTaskPlan(
    projectId: string,
    boardId: string,
    goal: string,
    timeZoneId: string,
  ): Promise<TaskPlanProposal> {
    const body: ApiTaskPlanProposeRequest = {
      goal,
      callerTimeZoneId: timeZoneId,
      callerLanguage: i18n.language ?? null,
    };

    const { data } = await httpClient.post<ApiTaskPlanProposeRequest, ApiTaskPlanProposalResponse>(
      aiTaskPlanProposalPath(projectId, boardId),
      body,
    );

    return {
      correlationId: data.correlationId,
      tasks: data.tasks.map((t) => ({
        title: t.title,
        description: t.description,
        priority: t.priority,
        estimatedMinutes: t.estimatedMinutes,
        estimateNote: t.estimateNote,
      })),
    };
  },

  async applyTaskPlan(
    projectId: string,
    boardId: string,
    request: ApiTaskPlanApplyRequest,
  ): Promise<ApplyTaskPlanResult> {
    const idempotencyKey = aiIdempotencyStore.getKey(projectId, boardId, 'goal-to-tasks');
    const { data } = await httpClient.post<ApiTaskPlanApplyRequest, ApiTaskPlanApplyResponse>(
      aiTaskPlanPath(projectId, boardId),
      request,
      { headers: { 'X-Idempotency-Key': idempotencyKey } },
    );
    return data;
  },

  async transcribeAudio(audio: Blob, language?: string): Promise<ApiSpeechToTextResponse> {
    if (env.VITE_USE_MOCK === 'true') {
      return { transcription: 'Mock transcription', confidence: 0.95, detectedLanguage: 'es-AR' };
    }

    const formData = new FormData();
    formData.append('audio', audio, 'recording.webm');
    if (language) {
      formData.append('language', language);
    }

    const { data } = await httpClient.post<FormData, ApiSpeechToTextResponse>(
      speechToTextPath(),
      formData,
    );
    return data;
  },
};
